import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";
import {
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Fade,
    IconButton,
    InputAdornment,
    Modal,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    FiClock,
    FiRefreshCw,
    FiSave,
    FiX,
    FiTrendingUp,
    FiTrendingDown,
    FiChevronLeft,
    FiChevronRight,
    FiSettings,
    FiBell,
    FiZap,
    FiCheckCircle,
    FiXCircle,
    FiExternalLink,
    FiSearch,
} from "react-icons/fi";

const TenantWallet = () => {
    const [tenants, setTenants] = useState([]);
    const [wallets, setWallets] = useState({});
    const [loading, setLoading] = useState(true);
    const [inputValues, setInputValues] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [activeTab, setActiveTab] = useState("credit"); // 'credit' or 'debit'
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [savingTenantId, setSavingTenantId] = useState(null);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [pendingRequestsMap, setPendingRequestsMap] = useState({});
    const [allRequests, setAllRequests] = useState([]);
    const [requestsModalOpen, setRequestsModalOpen] = useState(false);
    const [requestsModalTab, setRequestsModalTab] = useState("pending"); // 'pending' | 'approved' | 'rejected' | 'all'
    const [requestsSearch, setRequestsSearch] = useState("");
    const [processingRequestId, setProcessingRequestId] = useState(null);
    const [declineModalOpen, setDeclineModalOpen] = useState(false);
    const [requestToDecline, setRequestToDecline] = useState(null);
    const [declineReason, setDeclineReason] = useState("");
    const [showAlertBanner, setShowAlertBanner] = useState(true);

    const handleOpenRequestsModal = (tab = "pending") => {
        setRequestsModalTab(tab);
        setRequestsSearch("");
        setRequestsModalOpen(true);
    };
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPreviousPage: false
    });

    // Config modal state
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [selectedConfigTenant, setSelectedConfigTenant] = useState(null);
    const [companySpocEmail, setCompanySpocEmail] = useState("");
    const [csmEmail, setCsmEmail] = useState("");
    const [accountsEmail, setAccountsEmail] = useState("");
    const [thresholdBalance, setThresholdBalance] = useState("");
    const [configSaving, setConfigSaving] = useState(false);

    // Fetch all recharge requests (pending, approved, rejected)
    const fetchAllRequests = async () => {
        try {
            const response = await axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet/requests?status=all`,
                headers: {
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                },
            });

            if (response.data.success && Array.isArray(response.data.body)) {
                const reqs = response.data.body;
                setAllRequests(reqs);

                const pending = reqs.filter(
                    (r) => (r.status || "pending").toLowerCase() === "pending"
                );
                setPendingRequests(pending);

                const map = {};
                pending.forEach((r) => {
                    const tid = Number(r.tenant_id);
                    if (!map[tid]) {
                        map[tid] = [];
                    }
                    map[tid].push(r);
                });
                setPendingRequestsMap(map);
                return pending;
            }
        } catch (error) {
            console.error("Failed to fetch recharge requests:", error);
        }
        return [];
    };

    // Fetch tenants and wallet data
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        toast.remove();
        const toastId = toast.loading("Fetching tenant wallet data...");

        try {
            // Fetch tenants
            const tenantsResponse = await axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/admin/company`,
                headers: {
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                },
            });

            // Fetch wallets
            const walletsResponse = await axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet`,
                headers: {
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                },
            });

            // Fetch recharge requests
            const reqs = await fetchAllRequests();
            if (reqs && reqs.length > 0) {
                toast(
                    `🔔 Alert: ${reqs.length} pending recharge request${reqs.length > 1 ? "s" : ""} awaiting review!`,
                    {
                        duration: 6000,
                        style: {
                            background: "#fffbeb",
                            border: "1px solid #f59e0b",
                            color: "#92400e",
                            fontWeight: 700,
                            boxShadow: "0 10px 25px rgba(245, 158, 11, 0.2)",
                        },
                        icon: "⚡",
                    }
                );
            }

            if (tenantsResponse.data.success) {
                setTenants(tenantsResponse.data.body);

                // Create wallet map by tenant_id
                const walletMap = {};
                const initialInputs = {};

                if (walletsResponse.data.success && walletsResponse.data.body) {
                    walletsResponse.data.body.forEach((wallet) => {
                        walletMap[wallet.tenant_id] = wallet;
                        initialInputs[wallet.tenant_id] = "";
                    });
                }

                // Set initial input values for all tenants
                tenantsResponse.data.body.forEach((tenant) => {
                    if (!initialInputs[tenant.tenant_id]) {
                        initialInputs[tenant.tenant_id] = "";
                    }
                });

                setWallets(walletMap);
                setInputValues(initialInputs);
                toast.success("Data fetched successfully", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to fetch data",
                { id: toastId }
            );
        } finally {
            setLoading(false);
        }
    };

    const handleApproveRequest = async (req) => {
        setProcessingRequestId(req.id);
        const toastId = toast.loading(`Approving recharge of ₹${Number(req.amount).toLocaleString("en-IN")}...`);

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet/requests/${req.id}/approve`,
                headers: {
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                    "Content-Type": "application/json",
                },
            });

            if (response.data.success) {
                toast.success(
                    `Recharge request of ₹${Number(req.amount).toLocaleString("en-IN")} approved & credited!`,
                    { id: toastId }
                );

                // Update local wallet in state
                if (response.data.body?.wallet) {
                    setWallets((prev) => ({
                        ...prev,
                        [req.tenant_id]: response.data.body.wallet,
                    }));
                }

                // Refresh requests
                fetchAllRequests();

                // If modal is open for this tenant, refresh history
                if (showModal && selectedTenant && Number(selectedTenant.tenant_id) === Number(req.tenant_id)) {
                    fetchHistory(selectedTenant.tenant_id, activeTab, pagination.currentPage, selectedTenant);
                }
            } else {
                toast.error(response.data.message || "Failed to approve request", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to approve request",
                { id: toastId }
            );
        } finally {
            setProcessingRequestId(null);
        }
    };

    const handleOpenDeclineModal = (req) => {
        setRequestToDecline(req);
        setDeclineReason("");
        setDeclineModalOpen(true);
    };

    const handleConfirmDecline = async () => {
        if (!requestToDecline) return;
        setProcessingRequestId(requestToDecline.id);
        const toastId = toast.loading("Declining recharge request...");

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet/requests/${requestToDecline.id}/decline`,
                headers: {
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                    "Content-Type": "application/json",
                },
                data: {
                    reason: declineReason.trim() || "Declined by admin",
                },
            });

            if (response.data.success) {
                toast.success("Recharge request declined", { id: toastId });
                setDeclineModalOpen(false);
                setRequestToDecline(null);
                setDeclineReason("");

                fetchAllRequests();

                if (showModal && selectedTenant && Number(selectedTenant.tenant_id) === Number(requestToDecline.tenant_id)) {
                    fetchHistory(selectedTenant.tenant_id, activeTab, pagination.currentPage, selectedTenant);
                }
            } else {
                toast.error(response.data.message || "Failed to decline request", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to decline request",
                { id: toastId }
            );
        } finally {
            setProcessingRequestId(null);
        }
    };

    const handleInputChange = (tenantId, value) => {
        setInputValues((prev) => ({
            ...prev,
            [tenantId]: value,
        }));
    };

    const handleSaveWallet = async (tenant) => {
        const amount = Number(inputValues[tenant.tenant_id]);
        if (!amount || Number.isNaN(amount) || amount <= 0) {
            toast.error("Enter a valid credit amount");
            return;
        }

        setSavingTenantId(tenant.tenant_id);
        const toastId = toast.loading("Crediting wallet...");

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                    slug: tenant.slug || tenant.tenant_id || "",
                },
                data: {
                    tenant_id: tenant.tenant_id,
                    tenant_name: tenant.name,
                    wallet_balance: amount,
                },
            });

            if (response.data.success) {
                toast.success("Wallet credited successfully", { id: toastId });

                // Update local wallet state
                setWallets((prev) => ({
                    ...prev,
                    [tenant.tenant_id]: response.data.body,
                }));
                setInputValues((prev) => ({
                    ...prev,
                    [tenant.tenant_id]: "",
                }));

                // Refresh all requests to update alert
                fetchAllRequests();
            } else {
                toast.error(response.data.message || "Failed to save", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to save",
                { id: toastId }
            );
        } finally {
            setSavingTenantId(null);
        }
    };

    const handleOpenModal = (tenant, defaultTab = "credit") => {
        setSelectedTenant(tenant);
        setShowModal(true);
        setActiveTab(defaultTab);
        setPagination({
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: 10,
            hasNextPage: false,
            hasPreviousPage: false
        });
        fetchHistory(tenant.tenant_id, defaultTab, 1, tenant);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedTenant(null);
        setHistoryData([]);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPagination({
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: 10,
            hasNextPage: false,
            hasPreviousPage: false
        });
        fetchHistory(selectedTenant.tenant_id, tab, 1, selectedTenant);
    };

    const fetchHistory = async (tenantId, transactionType, page, tenantObj = selectedTenant) => {
        setHistoryLoading(true);
        try {
            const response = await axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet/${tenantId}/passbook`,
                headers: {
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                    slug: tenantObj?.slug || tenantObj?.tenant_id || tenantId || "",
                },
                params: {
                    transaction_type: transactionType,
                    page: page,
                    limit: 10
                }
            });

            if (response.data.success) {
                setHistoryData(response.data.body.data || []);
                setPagination(response.data.body.pagination || pagination);
            } else {
                toast.error(response.data.message || "Failed to fetch history");
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to fetch history"
            );
        } finally {
            setHistoryLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        fetchHistory(selectedTenant.tenant_id, activeTab, newPage, selectedTenant);
    };

    const handleOpenConfigModal = (tenant) => {
        if (!tenant) return;
        setSelectedConfigTenant(tenant);
        const wallet = wallets[tenant.tenant_id];
        setCompanySpocEmail(wallet?.company_spoc_email || wallet?.spoc_emails?.[0] || "");
        setCsmEmail(wallet?.csm_email || wallet?.spoc_emails?.[1] || "");
        setAccountsEmail(wallet?.accounts_email || wallet?.spoc_emails?.[2] || "");
        setThresholdBalance(
            wallet?.threshold_balance !== undefined && wallet?.threshold_balance !== null
                ? wallet.threshold_balance
                : ""
        );
        setShowConfigModal(true);
    };

    const handleCloseConfigModal = () => {
        setShowConfigModal(false);
        setSelectedConfigTenant(null);
        setCompanySpocEmail("");
        setCsmEmail("");
        setAccountsEmail("");
        setThresholdBalance("");
    };

    const handleSaveConfig = async () => {
        if (!selectedConfigTenant) {
            toast.error("Tenant not selected");
            return;
        }

        setConfigSaving(true);
        const toastId = toast.loading("Saving wallet details...");

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet/config`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                    slug: selectedConfigTenant.slug || selectedConfigTenant.tenant_id || "",
                },
                data: {
                    tenant_id: Number(selectedConfigTenant.tenant_id),
                    tenant_name: selectedConfigTenant.name,
                    company_spoc_email: companySpocEmail.trim(),
                    csm_email: csmEmail.trim(),
                    accounts_email: accountsEmail.trim(),
                    threshold_balance: thresholdBalance !== "" ? Number(thresholdBalance) : null,
                },
            });

            if (response.data.success) {
                toast.success("Wallet details saved successfully", { id: toastId });
                setWallets((prev) => ({
                    ...prev,
                    [selectedConfigTenant.tenant_id]: response.data.body,
                }));
                handleCloseConfigModal();
            } else {
                toast.error(response.data.message || "Failed to save details", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to save details",
                { id: toastId }
            );
        } finally {
            setConfigSaving(false);
        }
    };

    const columns = [
        {
            name: "S.No",
            selector: (row, idx) => idx + 1,
            sortable: true,
            width: "70px",
        },
        {
            name: "Tenant ID",
            selector: (row) => row.tenant_id,
            sortable: true,
            width: "100px",
        },
        {
            name: "Client Name",
            cell: (row) => {
                const tenantReqs = pendingRequestsMap[row.tenant_id] || [];
                const hasPending = tenantReqs.length > 0;
                const totalReqAmount = tenantReqs.reduce((sum, r) => sum + Number(r.amount || 0), 0);

                return (
                    <Box sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "rgba(15, 23, 42, 0.9)" }}>
                            {row.name}
                        </Typography>
                        {hasPending && (
                            <Box sx={{ mt: 0.5 }}>
                                <Tooltip title={`Pending recharge request of ₹${totalReqAmount.toLocaleString("en-IN")}. Click to fill & review.`} arrow>
                                    <Chip
                                        size="small"
                                        icon={<FiZap style={{ color: "#d97706" }} />}
                                        label={`Req: ₹ ${totalReqAmount.toLocaleString("en-IN")}`}
                                        onClick={() => {
                                            handleInputChange(row.tenant_id, tenantReqs[0].amount);
                                            handleOpenModal(row, "recharge_request");
                                        }}
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: "11px",
                                            height: "22px",
                                            cursor: "pointer",
                                            bgcolor: "#fef3c7",
                                            color: "#b45309",
                                            border: "1px solid #fde68a",
                                            boxShadow: "0 2px 6px rgba(245, 158, 11, 0.25)",
                                            "&:hover": {
                                                bgcolor: "#fde68a",
                                                transform: "translateY(-1px)",
                                            },
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                );
            },
            selector: (row) => row.name,
            sortable: true,
            minWidth: "190px",
        },
        {
            name: "Current Balance",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                return wallet ? `₹ ${Number(wallet.wallet_balance).toFixed(2)}` : "₹ 0.00";
            },
            width: "140px",
        },
        {
            name: "Threshold Balance",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                if (wallet?.threshold_balance !== undefined && wallet?.threshold_balance !== null) {
                    return `₹ ${Number(wallet.threshold_balance).toLocaleString("en-IN")}`;
                }
                return "-";
            },
            width: "150px",
        },
        {
            name: "SPOC Emails",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                const spoc = wallet?.company_spoc_email || wallet?.spoc_emails?.[0];
                const csm = wallet?.csm_email || wallet?.spoc_emails?.[1];
                const acc = wallet?.accounts_email || wallet?.spoc_emails?.[2];
                const hasAny = spoc || csm || acc;

                if (!hasAny) return "-";

                const tooltipContent = (
                    <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" display="block"><strong>SPOC:</strong> {spoc || "N/A"}</Typography>
                        <Typography variant="caption" display="block"><strong>CSM:</strong> {csm || "N/A"}</Typography>
                        <Typography variant="caption" display="block"><strong>Accounts:</strong> {acc || "N/A"}</Typography>
                    </Box>
                );

                return (
                    <Tooltip title={tooltipContent} arrow placement="top">
                        <Chip
                            size="small"
                            label={`${[spoc, csm, acc].filter(Boolean).length} Contact(s)`}
                            sx={{
                                fontWeight: 700,
                                bgcolor: "rgba(99, 102, 241, 0.1)",
                                color: "rgb(79, 70, 229)",
                                border: "1px solid rgba(99, 102, 241, 0.2)",
                            }}
                        />
                    </Tooltip>
                );
            },
            width: "140px",
        },
        {
            name: "Credit Amount",
            cell: (row) => {
                const tenantReqs = pendingRequestsMap[row.tenant_id] || [];
                const latestReq = tenantReqs[0];

                return (
                    <Box sx={{ py: 0.5 }}>
                        <TextField
                            type="number"
                            size="small"
                            value={inputValues[row.tenant_id] ?? ""}
                            onChange={(e) => handleInputChange(row.tenant_id, e.target.value)}
                            placeholder="0.00"
                            inputProps={{ step: "0.01", min: 0 }}
                            sx={{ width: 120 }}
                        />
                        {latestReq && (
                            <Box sx={{ mt: 0.5 }}>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handleInputChange(row.tenant_id, latestReq.amount)}
                                    sx={{
                                        fontSize: "10.5px",
                                        p: 0,
                                        minWidth: 0,
                                        textTransform: "none",
                                        color: "#d97706",
                                        fontWeight: 800,
                                        "&:hover": { textDecoration: "underline" }
                                    }}
                                >
                                    ⚡ Fill ₹{Number(latestReq.amount).toLocaleString("en-IN")}
                                </Button>
                            </Box>
                        )}
                    </Box>
                );
            },
            width: "150px",
        },
        {
            name: "Last Updated",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                return wallet?.updated_at ? new Date(wallet.updated_at).toLocaleString() : "-";
            },
            width: "180px",
        },
        {
            name: "Updated By",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                return wallet?.updated_by || "-";
            },
            width: "130px",
        },
        {
            name: "Config",
            cell: (row) => (
                <Tooltip title="Configure SPOC & Threshold">
                    <IconButton
                        onClick={() => handleOpenConfigModal(row)}
                        size="small"
                        sx={{
                            border: "1px solid",
                            borderColor: "rgba(99, 102, 241, 0.4)",
                            color: "rgb(79, 70, 229)",
                            transition: "transform 160ms ease, background-color 160ms ease",
                            "&:hover": {
                                transform: "translateY(-1px)",
                                backgroundColor: "rgba(99, 102, 241, 0.08)",
                            },
                        }}
                    >
                        <FiSettings />
                    </IconButton>
                </Tooltip>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "80px",
        },
        {
            name: "History",
            cell: (row) => (
                <Tooltip title="View passbook">
                    <IconButton
                        onClick={() => handleOpenModal(row)}
                        size="small"
                        sx={{
                            border: "1px solid",
                            borderColor: "rgba(2, 132, 199, 0.4)",
                            color: "rgb(2, 132, 199)",
                            transition: "transform 160ms ease, background-color 160ms ease",
                            "&:hover": {
                                transform: "translateY(-1px)",
                                backgroundColor: "rgba(2, 132, 199, 0.08)",
                            },
                        }}
                    >
                        <FiClock />
                    </IconButton>
                </Tooltip>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "80px",
        },
        {
            name: "Action",
            cell: (row) => {
                const tenantReqs = pendingRequestsMap[row.tenant_id] || [];
                const hasPending = tenantReqs.length > 0;

                return (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSaveWallet(row)}
                        disabled={
                            savingTenantId === row.tenant_id ||
                            !Number(inputValues[row.tenant_id]) ||
                            Number(inputValues[row.tenant_id]) <= 0
                        }
                        startIcon={
                            savingTenantId === row.tenant_id ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                <FiSave />
                            )
                        }
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            boxShadow: "none",
                            transition: "transform 160ms ease, box-shadow 160ms ease",
                            ...(hasPending && {
                                bgcolor: "#d97706",
                                "&:hover": {
                                    bgcolor: "#b45309",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 10px 25px rgba(217, 119, 6, 0.3)",
                                },
                            }),
                            ...(!hasPending && {
                                "&:hover": {
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 10px 25px rgba(2, 6, 23, 0.12)",
                                },
                            }),
                        }}
                    >
                        Credit
                    </Button>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "110px",
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 700,
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "rgba(15, 23, 42, 0.7)",
            },
        },
        rows: {
            style: {
                minHeight: "62px",
            },
        },
    };

    return (
        <div className="page-content">
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    background:
                        "radial-gradient(1200px circle at 0% 0%, rgba(56, 189, 248, 0.18), transparent 45%), radial-gradient(900px circle at 100% 0%, rgba(99, 102, 241, 0.16), transparent 45%), #fff",
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                            Tenant Wallet
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.65)", mt: 0.5 }}>
                            Credit wallets and review passbook entries.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {pendingRequests.length > 0 && (
                            <Chip
                                icon={<FiBell style={{ color: "#fff" }} />}
                                label={`${pendingRequests.length} Recharge Request${pendingRequests.length > 1 ? "s" : ""}`}
                                size="small"
                                onClick={() => setShowAlertBanner(true)}
                                sx={{
                                    fontWeight: 800,
                                    bgcolor: "#d97706",
                                    color: "#fff",
                                    cursor: "pointer",
                                    boxShadow: "0 4px 12px rgba(217, 119, 6, 0.35)",
                                    "&:hover": { bgcolor: "#b45309" },
                                }}
                            />
                        )}
                        <Chip
                            label={loading ? "Loading" : `${tenants.length} tenants`}
                            size="small"
                            sx={{
                                fontWeight: 700,
                                bgcolor: "rgba(2, 132, 199, 0.08)",
                                color: "rgb(2, 132, 199)",
                            }}
                        />
                        <Button
                            variant="outlined"
                            onClick={fetchData}
                            startIcon={<FiRefreshCw />}
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                transition: "transform 160ms ease",
                                "&:hover": { transform: "translateY(-1px)" },
                            }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>

                {/* Sleek, Compact Alert Banner for Recharge Requests */}
                {allRequests.length > 0 && (
                    <Collapse in={showAlertBanner}>
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 3,
                                p: { xs: 2, md: 2.25 },
                                borderRadius: 3,
                                border: pendingRequests.length > 0 ? "2px solid #f59e0b" : "1px solid rgba(15, 23, 42, 0.12)",
                                background:
                                    pendingRequests.length > 0
                                        ? "linear-gradient(135deg, #fffbeb 0%, #fef3c7 45%, #ffedd5 100%)"
                                        : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                boxShadow: "0 10px 25px -5px rgba(245, 158, 11, 0.15), 0 8px 10px -6px rgba(15, 23, 42, 0.08)",
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                alignItems={{ xs: "flex-start", md: "center" }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                {/* Left: Notification badge & info */}
                                <Stack direction="row" spacing={1.75} alignItems="center">
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 44,
                                            height: 44,
                                            borderRadius: "50%",
                                            bgcolor: pendingRequests.length > 0 ? "#d97706" : "#2563eb",
                                            color: "#fff",
                                            boxShadow: pendingRequests.length > 0 ? "0 0 0 5px rgba(245, 158, 11, 0.25)" : "none",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <FiBell size={22} />
                                    </Box>
                                    <Box>
                                        <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "16px" }}>
                                                Wallet Recharge Requests Management
                                            </Typography>
                                            {pendingRequests.length > 0 ? (
                                                <Chip
                                                    label={`${pendingRequests.length} Pending Approval`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: "#b45309",
                                                        color: "#fff",
                                                        fontWeight: 800,
                                                        fontSize: "11.5px",
                                                        height: "22px",
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    label="All Requests Reviewed"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: "rgba(34, 197, 94, 0.15)",
                                                        color: "#15803d",
                                                        fontWeight: 800,
                                                        fontSize: "11.5px",
                                                        height: "22px",
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                        <Typography variant="body2" sx={{ color: "#64748b", fontSize: "13px", mt: 0.25 }}>
                                            {pendingRequests.length > 0
                                                ? `${pendingRequests.length} tenant${pendingRequests.length > 1 ? "s have" : " has"} submitted wallet recharge requests awaiting review.`
                                                : "All tenant recharge requests have been resolved. Click to view history."}
                                        </Typography>
                                    </Box>
                                </Stack>

                                {/* Right: Quick Stats Chips & Modal Trigger Button */}
                                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                                    <Tooltip title="Click to open popup for pending requests">
                                        <Chip
                                            icon={<FiClock size={13} color="#d97706" />}
                                            label={`Pending (${pendingRequests.length})`}
                                            onClick={() => handleOpenRequestsModal("pending")}
                                            clickable
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "12px",
                                                bgcolor: "rgba(245, 158, 11, 0.15)",
                                                color: "#b45309",
                                                border: "1px solid rgba(245, 158, 11, 0.35)",
                                                "&:hover": { bgcolor: "rgba(245, 158, 11, 0.25)" },
                                            }}
                                        />
                                    </Tooltip>

                                    <Tooltip title="Click to open popup for approved requests">
                                        <Chip
                                            icon={<FiCheckCircle size={13} color="#15803d" />}
                                            label={`Approved (${allRequests.filter((r) => (r.status || "").toLowerCase() === "approved").length})`}
                                            onClick={() => handleOpenRequestsModal("approved")}
                                            clickable
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "12px",
                                                bgcolor: "rgba(34, 197, 94, 0.12)",
                                                color: "#15803d",
                                                border: "1px solid rgba(34, 197, 94, 0.35)",
                                                "&:hover": { bgcolor: "rgba(34, 197, 94, 0.2)" },
                                            }}
                                        />
                                    </Tooltip>

                                    <Tooltip title="Click to open popup for declined requests">
                                        <Chip
                                            icon={<FiXCircle size={13} color="#dc2626" />}
                                            label={`Declined (${allRequests.filter((r) => (r.status || "").toLowerCase() === "rejected").length})`}
                                            onClick={() => handleOpenRequestsModal("rejected")}
                                            clickable
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "12px",
                                                bgcolor: "rgba(239, 68, 68, 0.12)",
                                                color: "#dc2626",
                                                border: "1px solid rgba(239, 68, 68, 0.35)",
                                                "&:hover": { bgcolor: "rgba(239, 68, 68, 0.2)" },
                                            }}
                                        />
                                    </Tooltip>

                                    <Button
                                        variant="contained"
                                        onClick={() => handleOpenRequestsModal(pendingRequests.length > 0 ? "pending" : "all")}
                                        endIcon={<FiExternalLink />}
                                        sx={{
                                            fontWeight: 800,
                                            textTransform: "none",
                                            borderRadius: 2,
                                            px: 2,
                                            py: 0.75,
                                            fontSize: "13px",
                                            bgcolor: pendingRequests.length > 0 ? "#d97706" : "#2563eb",
                                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
                                            "&:hover": {
                                                bgcolor: pendingRequests.length > 0 ? "#b45309" : "#1d4ed8",
                                            },
                                        }}
                                    >
                                        View & Manage Requests
                                    </Button>

                                    <IconButton
                                        size="small"
                                        onClick={() => setShowAlertBanner(false)}
                                        title="Dismiss alert banner"
                                        sx={{
                                            color: "#64748b",
                                            "&:hover": { bgcolor: "rgba(15, 23, 42, 0.08)" },
                                        }}
                                    >
                                        <FiX size={18} />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Collapse>
                )}

                <Box
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        backgroundColor: "rgba(255,255,255,0.75)",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <DataTable
                        title=""
                        data={tenants}
                        columns={columns}
                        striped
                        highlightOnHover
                        pointerOnHover
                        pagination
                        progressPending={loading}
                        customStyles={customStyles}
                    />
                </Box>
            </Paper>

            {/* Passbook History Modal */}
            <Modal
                open={showModal}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={showModal}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "80%",
                            maxWidth: "900px",
                            maxHeight: "90vh",
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(12px)",
                            boxShadow: "0 30px 80px rgba(2, 6, 23, 0.35)",
                            borderRadius: 2,
                            p: 3,
                            overflowY: "auto",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 2 }}
                        >
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Passbook
                                </Typography>
                                <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.65)", mt: 0.25 }}>
                                    {selectedTenant?.name || ""}
                                </Typography>
                            </Box>
                            <Tooltip title="Close">
                                <IconButton
                                    onClick={handleCloseModal}
                                    sx={{
                                        border: "1px solid rgba(15, 23, 42, 0.12)",
                                        transition: "transform 160ms ease, background-color 160ms ease",
                                        "&:hover": {
                                            transform: "rotate(90deg)",
                                            backgroundColor: "rgba(15, 23, 42, 0.06)",
                                        },
                                    }}
                                >
                                    <FiX />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        {/* Tabs */}
                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => handleTabChange(v)}
                            sx={{
                                mb: 2,
                                "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
                            }}
                        >
                            <Tab value="credit" icon={<FiTrendingUp />} iconPosition="start" label="Credits" />
                            <Tab value="debit" icon={<FiTrendingDown />} iconPosition="start" label="Debits" />
                            <Tab value="recharge_request" icon={<FiClock />} iconPosition="start" label="Recharge Requests" />
                        </Tabs>

                        {/* History Table */}
                        {historyLoading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    py: 4,
                                }}
                            >
                                <CircularProgress size={28} />
                            </Box>
                        ) : historyData.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
                                    No {activeTab} history found
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    {activeTab === "recharge_request" ? (
                                        <table className="table table-striped table-hover align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Reference ID</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>Description</th>
                                                    <th>Resolution Details</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historyData.map((entry, idx) => {
                                                    const isPending = (entry.status || "pending").toLowerCase() === "pending";
                                                    const isApproved = (entry.status || "").toLowerCase() === "approved";
                                                    const isRejected = (entry.status || "").toLowerCase() === "rejected";

                                                    return (
                                                        <tr key={entry.id || idx}>
                                                            <td>{new Date(entry.created_at).toLocaleString()}</td>
                                                            <td>
                                                                <code style={{ fontSize: "11px", fontWeight: 700, color: "#475569" }}>
                                                                    {entry.reference_id || entry.id || "-"}
                                                                </code>
                                                            </td>
                                                            <td>
                                                                <Chip
                                                                    size="small"
                                                                    label={`₹ ${Number(entry.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                                                                    sx={{
                                                                        fontWeight: 800,
                                                                        bgcolor: isApproved
                                                                            ? "rgba(34, 197, 94, 0.12)"
                                                                            : isRejected
                                                                            ? "rgba(239, 68, 68, 0.12)"
                                                                            : "rgba(245, 158, 11, 0.12)",
                                                                        color: isApproved
                                                                            ? "rgb(22, 163, 74)"
                                                                            : isRejected
                                                                            ? "rgb(220, 38, 38)"
                                                                            : "rgb(217, 119, 6)",
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>
                                                                {isApproved ? (
                                                                    <Chip
                                                                        size="small"
                                                                        icon={<FiCheckCircle size={12} color="#15803d" />}
                                                                        label="Approved"
                                                                        sx={{
                                                                            bgcolor: "rgba(34, 197, 94, 0.12)",
                                                                            color: "#15803d",
                                                                            fontWeight: 800,
                                                                            border: "1px solid rgba(34, 197, 94, 0.3)",
                                                                        }}
                                                                    />
                                                                ) : isRejected ? (
                                                                    <Chip
                                                                        size="small"
                                                                        icon={<FiXCircle size={12} color="#dc2626" />}
                                                                        label="Declined"
                                                                        sx={{
                                                                            bgcolor: "rgba(239, 68, 68, 0.12)",
                                                                            color: "#dc2626",
                                                                            fontWeight: 800,
                                                                            border: "1px solid rgba(239, 68, 68, 0.3)",
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Chip
                                                                        size="small"
                                                                        icon={<FiClock size={12} color="#d97706" />}
                                                                        label="Pending"
                                                                        sx={{
                                                                            bgcolor: "rgba(245, 158, 11, 0.12)",
                                                                            color: "#b45309",
                                                                            fontWeight: 800,
                                                                            border: "1px solid rgba(245, 158, 11, 0.3)",
                                                                        }}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td style={{ maxWidth: 220 }}>
                                                                <span style={{ fontSize: "13px", color: "#334155" }}>
                                                                    {entry.description || "-"}
                                                                </span>
                                                            </td>
                                                            <td style={{ maxWidth: 220 }}>
                                                                {isApproved ? (
                                                                    <span style={{ fontSize: "12px", color: "#15803d", fontWeight: 600 }}>
                                                                        ✓ Credited to wallet
                                                                        {entry.resolved_at && ` on ${new Date(entry.resolved_at).toLocaleDateString()}`}
                                                                    </span>
                                                                ) : isRejected ? (
                                                                    <span style={{ fontSize: "12px", color: "#dc2626", fontWeight: 600 }}>
                                                                        {entry.rejection_reason ? `Declined: ${entry.rejection_reason}` : "Declined by admin"}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ fontSize: "12px", color: "#94a3b8", fontStyle: "italic" }}>
                                                                        Awaiting review
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {isPending ? (
                                                                    <Stack direction="row" spacing={1}>
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            disabled={processingRequestId === (entry.request_id || entry.id)}
                                                                            onClick={() =>
                                                                                handleApproveRequest({
                                                                                    id: entry.request_id || entry.reference_id,
                                                                                    tenant_id: selectedTenant?.tenant_id,
                                                                                    amount: entry.amount,
                                                                                })
                                                                            }
                                                                            startIcon={<FiCheckCircle />}
                                                                            sx={{
                                                                                bgcolor: "#16a34a",
                                                                                "&:hover": { bgcolor: "#15803d" },
                                                                                fontSize: "11px",
                                                                                textTransform: "none",
                                                                                py: 0.5,
                                                                                px: 1,
                                                                            }}
                                                                        >
                                                                            Approve
                                                                        </Button>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="error"
                                                                            disabled={processingRequestId === (entry.request_id || entry.id)}
                                                                            onClick={() =>
                                                                                handleOpenDeclineModal({
                                                                                    id: entry.request_id || entry.reference_id,
                                                                                    tenant_id: selectedTenant?.tenant_id,
                                                                                    amount: entry.amount,
                                                                                    description: entry.description,
                                                                                })
                                                                            }
                                                                            startIcon={<FiXCircle />}
                                                                            sx={{
                                                                                fontSize: "11px",
                                                                                textTransform: "none",
                                                                                py: 0.5,
                                                                                px: 1,
                                                                            }}
                                                                        >
                                                                            Decline
                                                                        </Button>
                                                                    </Stack>
                                                                ) : (
                                                                    <span style={{ fontSize: "12px", color: "#64748b" }}>Completed</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <table className="table table-striped table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Amount</th>
                                                    <th>Balance Before</th>
                                                    <th>Balance After</th>
                                                    <th>Reference Type</th>
                                                    <th>Reference ID</th>
                                                    <th>Description</th>
                                                    <th>Created By</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historyData.map((entry, idx) => (
                                                    <tr key={idx}>
                                                        <td>{new Date(entry.created_at).toLocaleString()}</td>
                                                        <td>
                                                            <Chip
                                                                size="small"
                                                                label={
                                                                    entry.transaction_type === "credit"
                                                                        ? `+ ₹ ${Number(entry.amount).toFixed(2)}`
                                                                        : entry.transaction_type === "debit"
                                                                        ? `- ₹ ${Number(entry.amount).toFixed(2)}`
                                                                        : `₹ ${Number(entry.amount).toFixed(2)} (Req)`
                                                                }
                                                                sx={{
                                                                    fontWeight: 800,
                                                                    bgcolor:
                                                                        entry.transaction_type === "credit"
                                                                            ? "rgba(34, 197, 94, 0.12)"
                                                                            : entry.transaction_type === "recharge_request"
                                                                            ? "rgba(245, 158, 11, 0.12)"
                                                                            : "rgba(239, 68, 68, 0.12)",
                                                                    color:
                                                                        entry.transaction_type === "credit"
                                                                            ? "rgb(22, 163, 74)"
                                                                            : entry.transaction_type === "recharge_request"
                                                                            ? "rgb(217, 119, 6)"
                                                                            : "rgb(220, 38, 38)",
                                                                }}
                                                            />
                                                        </td>
                                                        <td>₹ {Number(entry.balance_before).toFixed(2)}</td>
                                                        <td>₹ {Number(entry.balance_after).toFixed(2)}</td>
                                                        <td>
                                                            <Chip
                                                                size="small"
                                                                label={entry.reference_type || "-"}
                                                                sx={{
                                                                    bgcolor: "rgba(15, 23, 42, 0.08)",
                                                                    fontWeight: 700,
                                                                }}
                                                            />
                                                        </td>
                                                        <td>{entry.reference_id || "-"}</td>
                                                        <td>{entry.description || "-"}</td>
                                                        <td>{entry.created_by || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* Pagination */}
                                {pagination.totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            <span className="text-muted">
                                                Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{" "}
                                                {Math.min(
                                                    pagination.currentPage * pagination.limit,
                                                    pagination.totalCount
                                                )}{" "}
                                                of {pagination.totalCount} entries
                                            </span>
                                        </div>
                                        <nav>
                                            <ul className="pagination mb-0">
                                                <li
                                                    className={`page-item ${
                                                        !pagination.hasPreviousPage ? "disabled" : ""
                                                    }`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                        disabled={!pagination.hasPreviousPage}
                                                    >
                                                        <span
                                                            className="d-inline-flex align-items-center"
                                                            style={{ gap: 6 }}
                                                        >
                                                            <FiChevronLeft />
                                                            Previous
                                                        </span>
                                                    </button>
                                                </li>
                                                {[...Array(pagination.totalPages)].map((_, idx) => {
                                                    const page = idx + 1;
                                                    if (
                                                        page === 1 ||
                                                        page === pagination.totalPages ||
                                                        (page >= pagination.currentPage - 1 &&
                                                            page <= pagination.currentPage + 1)
                                                    ) {
                                                        return (
                                                            <li
                                                                key={page}
                                                                className={`page-item ${
                                                                    pagination.currentPage === page ? "active" : ""
                                                                }`}
                                                            >
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(page)}
                                                                >
                                                                    {page}
                                                                </button>
                                                            </li>
                                                        );
                                                    } else if (
                                                        page === pagination.currentPage - 2 ||
                                                        page === pagination.currentPage + 2
                                                    ) {
                                                        return (
                                                            <li key={page} className="page-item disabled">
                                                                <span className="page-link">...</span>
                                                            </li>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                <li
                                                    className={`page-item ${!pagination.hasNextPage ? "disabled" : ""}`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                        disabled={!pagination.hasNextPage}
                                                    >
                                                        <span
                                                            className="d-inline-flex align-items-center"
                                                            style={{ gap: 6 }}
                                                        >
                                                            Next
                                                            <FiChevronRight />
                                                        </span>
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </>
                        )}
                    </Box>
                </Fade>
            </Modal>

            {/* Wallet Recharge Requests Management Popup Modal */}
            <Modal
                open={requestsModalOpen}
                onClose={() => setRequestsModalOpen(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 400,
                }}
            >
                <Fade in={requestsModalOpen}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "92%",
                            maxWidth: "1050px",
                            maxHeight: "90vh",
                            display: "flex",
                            flexDirection: "column",
                            bgcolor: "rgba(255, 255, 255, 0.98)",
                            backdropFilter: "blur(20px)",
                            boxShadow: "0 30px 90px rgba(15, 23, 42, 0.35)",
                            borderRadius: 3.5,
                            p: { xs: 2.5, md: 3.5 },
                            border: "1px solid rgba(226, 232, 240, 0.9)",
                            overflow: "hidden",
                        }}
                    >
                        {/* Modal Header */}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ pb: 2, borderBottom: "1px solid rgba(226, 232, 240, 0.8)" }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: "50%",
                                        bgcolor: pendingRequests.length > 0 ? "rgba(245, 158, 11, 0.15)" : "rgba(37, 99, 235, 0.12)",
                                        color: pendingRequests.length > 0 ? "#d97706" : "#2563eb",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <FiBell size={22} />
                                </Box>
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                            Wallet Recharge Requests Management
                                        </Typography>
                                        {pendingRequests.length > 0 && (
                                            <Chip
                                                label={`${pendingRequests.length} Pending`}
                                                size="small"
                                                sx={{
                                                    bgcolor: "#b45309",
                                                    color: "#fff",
                                                    fontWeight: 800,
                                                    fontSize: "11px",
                                                    height: "22px",
                                                }}
                                            />
                                        )}
                                    </Stack>
                                    <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
                                        Review, approve, or decline tenant recharge requests. Approved requests immediately credit the tenant's wallet balance.
                                    </Typography>
                                </Box>
                            </Stack>

                            <IconButton
                                onClick={() => setRequestsModalOpen(false)}
                                sx={{
                                    border: "1px solid rgba(15, 23, 42, 0.12)",
                                    "&:hover": {
                                        backgroundColor: "rgba(15, 23, 42, 0.06)",
                                        transform: "rotate(90deg)",
                                    },
                                    transition: "all 160ms ease",
                                }}
                            >
                                <FiX />
                            </IconButton>
                        </Stack>

                        {/* Search and Tabs Row */}
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", md: "center" }}
                            spacing={2}
                            sx={{ mt: 2, mb: 2 }}
                        >
                            {/* Tabs */}
                            <Tabs
                                value={requestsModalTab}
                                onChange={(_, val) => setRequestsModalTab(val)}
                                sx={{
                                    minHeight: 40,
                                    "& .MuiTab-root": {
                                        textTransform: "none",
                                        fontWeight: 800,
                                        fontSize: "13px",
                                        minHeight: 40,
                                        py: 0.5,
                                        px: 1.5,
                                    },
                                }}
                            >
                                <Tab
                                    value="pending"
                                    label={`Pending (${pendingRequests.length})`}
                                    icon={<FiClock size={15} color="#d97706" />}
                                    iconPosition="start"
                                />
                                <Tab
                                    value="approved"
                                    label={`Approved (${allRequests.filter((r) => (r.status || "").toLowerCase() === "approved").length})`}
                                    icon={<FiCheckCircle size={15} color="#15803d" />}
                                    iconPosition="start"
                                />
                                <Tab
                                    value="rejected"
                                    label={`Declined (${allRequests.filter((r) => (r.status || "").toLowerCase() === "rejected").length})`}
                                    icon={<FiXCircle size={15} color="#dc2626" />}
                                    iconPosition="start"
                                />
                                <Tab
                                    value="all"
                                    label={`All (${allRequests.length})`}
                                    iconPosition="start"
                                />
                            </Tabs>

                            {/* Search Filter */}
                            <TextField
                                size="small"
                                placeholder="Search by tenant, ID, ref, note..."
                                value={requestsSearch}
                                onChange={(e) => setRequestsSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiSearch color="#94a3b8" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                        </Stack>

                        {/* Scrollable Requests List */}
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                pr: 0.5,
                                maxHeight: "58vh",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                            }}
                        >
                            {allRequests
                                .filter((req) => {
                                    const st = (req.status || "pending").toLowerCase();
                                    if (requestsModalTab !== "all" && st !== requestsModalTab) {
                                        return false;
                                    }
                                    if (!requestsSearch.trim()) return true;
                                    const q = requestsSearch.toLowerCase();
                                    return (
                                        String(req.tenant_name || "").toLowerCase().includes(q) ||
                                        String(req.tenant_id || "").toLowerCase().includes(q) ||
                                        String(req.reference_id || "").toLowerCase().includes(q) ||
                                        String(req.description || "").toLowerCase().includes(q) ||
                                        String(req.rejection_reason || "").toLowerCase().includes(q)
                                    );
                                })
                                .map((req) => {
                                    const matchedTenant = tenants.find((t) => Number(t.tenant_id) === Number(req.tenant_id)) || {
                                        tenant_id: req.tenant_id,
                                        name: req.tenant_name || `Tenant #${req.tenant_id}`,
                                    };

                                    const isPending = (req.status || "pending").toLowerCase() === "pending";
                                    const isApproved = (req.status || "").toLowerCase() === "approved";
                                    const isRejected = (req.status || "").toLowerCase() === "rejected";

                                    return (
                                        <Paper
                                            key={req.id || req.reference_id}
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2.5,
                                                bgcolor: isPending ? "rgba(255, 251, 235, 0.6)" : isApproved ? "rgba(240, 253, 244, 0.6)" : "rgba(254, 242, 242, 0.6)",
                                                border: isPending
                                                    ? "1.5px solid rgba(245, 158, 11, 0.35)"
                                                    : isApproved
                                                    ? "1px solid rgba(34, 197, 94, 0.3)"
                                                    : "1px solid rgba(239, 68, 68, 0.3)",
                                                display: "flex",
                                                flexDirection: { xs: "column", md: "row" },
                                                alignItems: { xs: "flex-start", md: "center" },
                                                justifyContent: "space-between",
                                                gap: 1.5,
                                                transition: "box-shadow 160ms ease",
                                                "&:hover": {
                                                    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.06)",
                                                },
                                            }}
                                        >
                                            {/* Details Left */}
                                            <Box sx={{ flex: 1 }}>
                                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "14.5px" }}>
                                                        {matchedTenant.name}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        label={`Tenant ID: ${req.tenant_id}`}
                                                        sx={{ height: 21, fontSize: "11px", fontWeight: 700, bgcolor: "rgba(15, 23, 42, 0.07)" }}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        label={`₹ ${Number(req.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                                                        sx={{
                                                            height: 23,
                                                            fontSize: "13px",
                                                            fontWeight: 800,
                                                            bgcolor: isApproved ? "#dcfce7" : isRejected ? "#fee2e2" : "#fef3c7",
                                                            color: isApproved ? "#15803d" : isRejected ? "#b91c1c" : "#b45309",
                                                            border: isApproved ? "1px solid #86efac" : isRejected ? "1px solid #fca5a5" : "1px solid #fde68a",
                                                        }}
                                                    />
                                                    {isApproved && (
                                                        <Chip
                                                            size="small"
                                                            icon={<FiCheckCircle size={13} color="#15803d" />}
                                                            label="Approved & Credited"
                                                            sx={{ height: 23, fontSize: "11px", fontWeight: 800, bgcolor: "rgba(34, 197, 94, 0.15)", color: "#15803d" }}
                                                        />
                                                    )}
                                                    {isRejected && (
                                                        <Chip
                                                            size="small"
                                                            icon={<FiXCircle size={13} color="#dc2626" />}
                                                            label="Declined"
                                                            sx={{ height: 23, fontSize: "11px", fontWeight: 800, bgcolor: "rgba(239, 68, 68, 0.15)", color: "#b91c1c" }}
                                                        />
                                                    )}
                                                    {isPending && (
                                                        <Chip
                                                            size="small"
                                                            icon={<FiClock size={13} color="#d97706" />}
                                                            label="Pending Approval"
                                                            sx={{ height: 23, fontSize: "11px", fontWeight: 800, bgcolor: "rgba(245, 158, 11, 0.15)", color: "#b45309" }}
                                                        />
                                                    )}
                                                </Stack>

                                                <Typography variant="body2" sx={{ color: "#334155", mt: 0.75, fontWeight: 500 }}>
                                                    {req.description ? <strong>"{req.description}" • </strong> : ""}
                                                    Requested on {new Date(req.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} • Ref: <code>{req.reference_id}</code>
                                                    {req.updated_at && !isPending && ` • Action on ${new Date(req.updated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`}
                                                </Typography>

                                                {isRejected && (
                                                    <Typography variant="body2" sx={{ color: "#dc2626", fontWeight: 600, mt: 0.5 }}>
                                                        Reason: {req.rejection_reason || "Declined by admin"}
                                                    </Typography>
                                                )}
                                                {isApproved && (
                                                    <Typography variant="body2" sx={{ color: "#15803d", fontWeight: 600, mt: 0.5 }}>
                                                        ✓ Wallet balance was credited by {req.updated_by || "Admin"}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Actions Right */}
                                            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setRequestsModalOpen(false);
                                                        handleOpenModal(matchedTenant, "recharge_request");
                                                    }}
                                                    startIcon={<FiClock />}
                                                    sx={{
                                                        textTransform: "none",
                                                        borderRadius: 2,
                                                        fontSize: "12px",
                                                        color: "#475569",
                                                        borderColor: "rgba(71, 85, 105, 0.3)",
                                                        fontWeight: 700,
                                                        "&:hover": {
                                                            borderColor: "#334155",
                                                            bgcolor: "rgba(15, 23, 42, 0.04)",
                                                        },
                                                    }}
                                                >
                                                    Passbook
                                                </Button>

                                                {isPending && (
                                                    <>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleOpenDeclineModal(req)}
                                                            disabled={processingRequestId === req.id}
                                                            startIcon={<FiXCircle />}
                                                            sx={{
                                                                textTransform: "none",
                                                                borderRadius: 2,
                                                                fontSize: "12px",
                                                                color: "#dc2626",
                                                                borderColor: "rgba(220, 38, 38, 0.5)",
                                                                fontWeight: 700,
                                                                "&:hover": {
                                                                    borderColor: "#dc2626",
                                                                    bgcolor: "rgba(220, 38, 38, 0.08)",
                                                                },
                                                            }}
                                                        >
                                                            Decline
                                                        </Button>

                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            onClick={() => handleApproveRequest(req)}
                                                            disabled={processingRequestId === req.id}
                                                            startIcon={
                                                                processingRequestId === req.id ? (
                                                                    <CircularProgress size={14} color="inherit" />
                                                                ) : (
                                                                    <FiCheckCircle />
                                                                )
                                                            }
                                                            sx={{
                                                                textTransform: "none",
                                                                borderRadius: 2,
                                                                fontSize: "12px",
                                                                fontWeight: 800,
                                                                bgcolor: "#16a34a",
                                                                boxShadow: "none",
                                                                "&:hover": {
                                                                    bgcolor: "#15803d",
                                                                    boxShadow: "0 4px 12px rgba(22, 163, 74, 0.35)",
                                                                },
                                                            }}
                                                        >
                                                            Approve & Credit
                                                        </Button>
                                                    </>
                                                )}
                                            </Stack>
                                        </Paper>
                                    );
                                })}

                            {allRequests.filter((req) => {
                                const st = (req.status || "pending").toLowerCase();
                                if (requestsModalTab !== "all" && st !== requestsModalTab) return false;
                                if (!requestsSearch.trim()) return true;
                                const q = requestsSearch.toLowerCase();
                                return (
                                    String(req.tenant_name || "").toLowerCase().includes(q) ||
                                    String(req.tenant_id || "").toLowerCase().includes(q) ||
                                    String(req.reference_id || "").toLowerCase().includes(q) ||
                                    String(req.description || "").toLowerCase().includes(q)
                                );
                            }).length === 0 && (
                                <Box sx={{ textAlign: "center", py: 6, bgcolor: "#f8fafc", borderRadius: 3 }}>
                                    <FiCheckCircle size={32} color="#94a3b8" />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#475569", mt: 1 }}>
                                        No {requestsModalTab !== "all" ? requestsModalTab : ""} requests found
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#94a3b8", mt: 0.25 }}>
                                        {requestsSearch ? "Try clearing your search query." : "No records currently under this tab."}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Modal Footer */}
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ pt: 2.5, mt: 2, borderTop: "1px solid rgba(226, 232, 240, 0.8)" }}
                        >
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                                Showing {allRequests.filter((req) => (requestsModalTab === "all" ? true : (req.status || "pending").toLowerCase() === requestsModalTab)).length} of {allRequests.length} total recharge requests
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => setRequestsModalOpen(false)}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, px: 2.5 }}
                            >
                                Close
                            </Button>
                        </Stack>
                    </Box>
                </Fade>
            </Modal>

            {/* Configure Tenant Wallet Modal */}
            <Modal
                open={showConfigModal}
                onClose={handleCloseConfigModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={showConfigModal}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "90%",
                            maxWidth: "550px",
                            bgcolor: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(16px)",
                            boxShadow: "0 30px 80px rgba(2, 6, 23, 0.35)",
                            borderRadius: 3,
                            p: 3.5,
                            border: "1px solid rgba(255, 255, 255, 0.8)",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 3 }}
                        >
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Wallet Configuration
                                </Typography>
                                <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.65)", mt: 0.25 }}>
                                    Set SPOC contacts and threshold balance for tenant wallet.
                                </Typography>
                            </Box>
                            <Tooltip title="Close">
                                <IconButton
                                    onClick={handleCloseConfigModal}
                                    sx={{
                                        border: "1px solid rgba(15, 23, 42, 0.12)",
                                        transition: "transform 160ms ease, background-color 160ms ease",
                                        "&:hover": {
                                            transform: "rotate(90deg)",
                                            backgroundColor: "rgba(15, 23, 42, 0.06)",
                                        },
                                    }}
                                >
                                    <FiX />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <Stack spacing={2.5}>
                            {selectedConfigTenant && (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: "rgba(2, 132, 199, 0.06)",
                                        border: "1px solid rgba(2, 132, 199, 0.15)",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(15, 23, 42, 0.6)",
                                            textTransform: "uppercase",
                                            fontWeight: 700,
                                            letterSpacing: "0.05em",
                                        }}
                                    >
                                        Tenant
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: "rgb(2, 132, 199)", mt: 0.25 }}>
                                        {selectedConfigTenant.name}{" "}
                                        <Typography component="span" variant="body2" sx={{ color: "rgba(15, 23, 42, 0.6)", fontWeight: 500 }}>
                                            (ID: {selectedConfigTenant.tenant_id})
                                        </Typography>
                                    </Typography>
                                </Box>
                            )}

                            <TextField
                                label="Company SPOC Email"
                                placeholder="spoc@company.com"
                                fullWidth
                                size="small"
                                type="email"
                                value={companySpocEmail}
                                onChange={(e) => setCompanySpocEmail(e.target.value)}
                            />

                            <TextField
                                label="CSM Email"
                                placeholder="csm@company.com"
                                fullWidth
                                size="small"
                                type="email"
                                value={csmEmail}
                                onChange={(e) => setCsmEmail(e.target.value)}
                            />

                            <TextField
                                label="Accounts Team Email"
                                placeholder="accounts@company.com"
                                fullWidth
                                size="small"
                                type="email"
                                value={accountsEmail}
                                onChange={(e) => setAccountsEmail(e.target.value)}
                            />

                            <TextField
                                label="Threshold Balance (₹)"
                                placeholder="10000"
                                fullWidth
                                size="small"
                                type="number"
                                inputProps={{ min: 0, step: "1" }}
                                value={thresholdBalance}
                                onChange={(e) => setThresholdBalance(e.target.value)}
                            />

                            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCloseConfigModal}
                                    disabled={configSaving}
                                    sx={{ textTransform: "none", borderRadius: 2 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveConfig}
                                    disabled={configSaving || !selectedConfigTenant}
                                    startIcon={
                                        configSaving ? (
                                            <CircularProgress size={14} color="inherit" />
                                        ) : (
                                            <FiSave />
                                        )
                                    }
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: 2,
                                        boxShadow: "none",
                                        background: "linear-gradient(135deg, #0284c7 0%, #2563eb 100%)",
                                    }}
                                >
                                    Save Details
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Fade>
            </Modal>

            {/* Decline Recharge Request Modal */}
            <Modal
                open={declineModalOpen}
                onClose={() => {
                    if (!processingRequestId) {
                        setDeclineModalOpen(false);
                        setRequestToDecline(null);
                        setDeclineReason("");
                    }
                }}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 400,
                }}
            >
                <Fade in={declineModalOpen}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "90%",
                            maxWidth: "480px",
                            bgcolor: "rgba(255, 255, 255, 0.98)",
                            backdropFilter: "blur(16px)",
                            boxShadow: "0 25px 60px rgba(15, 23, 42, 0.3)",
                            borderRadius: 3,
                            p: 3.5,
                            border: "1px solid rgba(239, 68, 68, 0.2)",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 2 }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "50%",
                                        bgcolor: "rgba(239, 68, 68, 0.12)",
                                        color: "#dc2626",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <FiXCircle size={20} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                    Decline Recharge Request
                                </Typography>
                            </Stack>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    if (!processingRequestId) {
                                        setDeclineModalOpen(false);
                                        setRequestToDecline(null);
                                        setDeclineReason("");
                                    }
                                }}
                                disabled={Boolean(processingRequestId)}
                                sx={{ color: "#64748b" }}
                            >
                                <FiX />
                            </IconButton>
                        </Stack>

                        {requestToDecline && (
                            <Box
                                sx={{
                                    p: 2,
                                    mb: 2.5,
                                    borderRadius: 2,
                                    bgcolor: "#fef2f2",
                                    border: "1px solid #fecaca",
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#991b1b" }}>
                                    Amount: ₹ {Number(requestToDecline.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#7f1d1d", display: "block", mt: 0.5 }}>
                                    Ref: <code>{requestToDecline.reference_id || requestToDecline.id}</code>
                                    {requestToDecline.description && ` • "${requestToDecline.description}"`}
                                </Typography>
                            </Box>
                        )}

                        <Typography variant="body2" sx={{ color: "#475569", mb: 1.5 }}>
                            Provide a reason for declining this request (optional, visible to tenant):
                        </Typography>

                        <TextField
                            label="Decline Reason"
                            placeholder="e.g. Invalid transaction reference, payment verification failed"
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            value={declineReason}
                            onChange={(e) => setDeclineReason(e.target.value)}
                            sx={{ mb: 3 }}
                        />

                        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setDeclineModalOpen(false);
                                    setRequestToDecline(null);
                                    setDeclineReason("");
                                }}
                                disabled={Boolean(processingRequestId)}
                                sx={{ textTransform: "none", borderRadius: 2 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleConfirmDecline}
                                disabled={Boolean(processingRequestId)}
                                startIcon={
                                    processingRequestId ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <FiXCircle />
                                    )
                                }
                                sx={{
                                    textTransform: "none",
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    boxShadow: "none",
                                }}
                            >
                                {processingRequestId ? "Declining..." : "Decline Request"}
                            </Button>
                        </Stack>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
};

export default TenantWallet;
